import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock, ChevronDown, ChevronUp, MessageSquare, Send, Mail, FileQuestion } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'react-toastify';

import { Card, Button, Badge, AreaChip, Select, Textarea, Skeleton } from '../ui-v2';
import { getIcon } from '../inventario/icons';
import { UsuarioContext } from '../../providers/personaContext';
import useImagenBlob from '../../../funciones/useImagenBlob';
import { getPanelTicket, patchPanelTicket, crearComentarioPanel, getPanelEquipo } from './api';

// MANTENER SYNCED con backend services/flujosEstado.js
const FLUJOS = { incidencia: ['pendiente', 'en_revision', 'en_desarrollo', 'resuelto'], sugerencia: ['recibida', 'en_estudio', 'aceptada', 'implementada'] };
const LATERALES = { incidencia: ['rechazado', 'descartado'], sugerencia: ['rechazada'] };
const ESTADO_META = {
  pendiente: { label: 'Pendiente', variant: 'warning' }, en_revision: { label: 'En revisión', variant: 'default' },
  en_desarrollo: { label: 'En desarrollo', variant: 'default' }, resuelto: { label: 'Resuelto', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'destructive' }, descartado: { label: 'Descartado', variant: 'secondary' },
  recibida: { label: 'Recibida', variant: 'secondary' }, en_estudio: { label: 'En estudio', variant: 'default' },
  aceptada: { label: 'Aceptada', variant: 'success' }, implementada: { label: 'Implementada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'destructive' },
};
const PRIORIDADES = ['baja', 'media', 'alta', 'critica'];
const PRIORIDAD_LABEL = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };
const PRIORIDAD_VARIANT = { baja: 'secondary', media: 'warning', alta: 'destructive', critica: 'destructive' };
const ESTADOS_EMAIL = ['en_desarrollo', 'aceptada', 'resuelto', 'implementada'];

function esTerminal(tipo, estado) {
  return LATERALES[tipo].includes(estado) || estado === FLUJOS[tipo][FLUJOS[tipo].length - 1];
}
// Estados a los que se puede transicionar desde `actual` (espejo de puedeTransicionar).
function transicionesPosibles(tipo, actual) {
  if (esTerminal(tipo, actual)) return [];
  const pr = FLUJOS[tipo]; const i = pr.indexOf(actual);
  const out = [];
  if (i > 0) out.push(pr[i - 1]);
  if (i < pr.length - 1) out.push(pr[i + 1]);
  return [...out, ...LATERALES[tipo]];
}
function fecha(f) { if (!f) return null; try { return format(new Date(f), "d MMM yyyy, HH:mm", { locale: es }); } catch { return null; } }
function hace(f) { try { return formatDistanceToNow(new Date(f), { addSuffix: true, locale: es }); } catch { return ''; } }

export default function SoportePanelDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { persona, permisos } = useContext(UsuarioContext);
  const puedeGestionar = (persona?.rol?.toLowerCase() === 'super administrador') || (permisos || []).includes('soporte.panel.manage');

  const [ticket, setTicket] = useState(null);
  const [equipo, setEquipo] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [noEncontrado, setNoEncontrado] = useState(false);

  // gestión local
  const [estadoSel, setEstadoSel] = useState('');
  const [notaCambio, setNotaCambio] = useState('');
  const [errNota, setErrNota] = useState(null);
  const [notasInput, setNotasInput] = useState('');
  const [techAbierto, setTechAbierto] = useState(false);
  const [tab, setTab] = useState('conv');
  const [nuevoPub, setNuevoPub] = useState('');
  const [nuevaInt, setNuevaInt] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [accion, setAccion] = useState(null); // qué control está guardando → spinner localizado

  const cargar = useCallback(() => {
    setCargando(true); setNoEncontrado(false);
    return getPanelTicket(id)
      .then((t) => { setTicket(t); setEstadoSel(t.estado); setNotasInput(t.notas_internas || ''); })
      .catch(() => setNoEncontrado(true))
      .finally(() => setCargando(false));
  }, [id]);

  const cargarEquipo = useCallback(() => { getPanelEquipo().then((e) => setEquipo(e.equipo || [])).catch(() => {}); }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (puedeGestionar) cargarEquipo(); }, [puedeGestionar, cargarEquipo]);

  async function aplicarPatch(payload, okMsg, campo = null) {
    setGuardando(true); setAccion(campo);
    try {
      await patchPanelTicket(id, payload);
      toast.success(okMsg);
      await cargar();
      return true;
    } catch (e) {
      const d = e?.response?.data;
      if (d?.error === 'tecnico_no_valido') { toast.error('Esta persona ya no es técnico activo.'); cargarEquipo(); }
      else if (d?.error === 'transicion_invalida') toast.error(d.message);
      else if (d?.error === 'nota_cambio_invalida') setErrNota(d.message);
      else if (d?.error === 'sin_cambios') toast.warn('No hay cambios que guardar.');
      else toast.error(d?.message || 'No se pudo guardar.');
      return false;
    } finally { setGuardando(false); setAccion(null); }
  }

  async function guardarEstado() {
    setErrNota(null);
    const payload = { estado: estadoSel };
    if (notaCambio.trim()) payload.nota_cambio = notaCambio.trim();
    const ok = await aplicarPatch(payload, 'Estado actualizado', 'estado');
    if (ok) setNotaCambio('');
  }
  const cambiarAsignado = (v) => aplicarPatch({ asignado_a: v === '' ? null : Number(v) }, 'Asignación actualizada', 'asignado');
  const cambiarPrioridad = (v) => aplicarPatch({ prioridad: v === '' ? null : v }, 'Prioridad actualizada', 'prioridad');
  const guardarNotas = () => aplicarPatch({ notas_internas: notasInput }, 'Notas internas guardadas', 'notas');

  async function comentar(contenido, es_interno, limpiar, campo = null) {
    if (!contenido.trim()) return;
    setGuardando(true); setAccion(campo);
    try {
      await crearComentarioPanel(id, { contenido: contenido.trim(), es_interno });
      limpiar('');
      await cargar();
    } catch (e) { toast.error(e?.response?.data?.message || 'No se pudo añadir el comentario.'); }
    finally { setGuardando(false); setAccion(null); }
  }

  if (cargando) return <SkeletonDetalle />;
  if (noEncontrado || !ticket) {
    return (
      <div className="px-6 py-16 text-center">
        <FileQuestion className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-700">Ticket no encontrado</p>
        <p className="text-sm text-slate-500 mt-1">El ticket #{id} no existe o ha sido eliminado.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/soporte/panel')}><ArrowLeft className="w-4 h-4" />Volver al panel</Button>
      </div>
    );
  }

  const est = ESTADO_META[ticket.estado] || { label: ticket.estado, variant: 'secondary' };
  const terminal = esTerminal(ticket.tipo, ticket.estado);
  const adjuntos = ticket.adjuntos || [];
  const opcionesEstado = transicionesPosibles(ticket.tipo, ticket.estado);

  return (
    <div className="px-6 py-6 min-h-full">
      <button type="button" onClick={() => navigate('/soporte/panel')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Panel de soporte / #{ticket.numero}
      </button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <code className="text-[11px] font-mono text-slate-400">#{ticket.numero}</code>
          <h1 className="text-xl font-semibold text-slate-900 mt-0.5">{ticket.titulo}</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className="text-[10px]">{ticket.tipo === 'incidencia' ? 'Incidencia' : 'Sugerencia'}</Badge>
          {ticket.prioridad && <Badge variant={PRIORIDAD_VARIANT[ticket.prioridad]}>{PRIORIDAD_LABEL[ticket.prioridad]}</Badge>}
          <Badge variant={est.variant}>{est.label}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Descripción */}
          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {ticket.categoria && <AreaChip area={ticket.categoria.color || 'otros'} icon={ticket.categoria.icono ? React.createElement(getIcon(ticket.categoria.icono), { className: 'w-3 h-3' }) : null}>{ticket.categoria.nombre}</AreaChip>}
              {ticket.modulo_intranet && <span className="text-xs text-slate-500">Módulo: {ticket.modulo_intranet}</span>}
              {ticket.tipo_dispositivo && <span className="text-xs text-slate-500">Dispositivo: {ticket.tipo_dispositivo}</span>}
              <span className="text-xs text-slate-400">· {hace(ticket.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-medium text-slate-900">{ticket.autor?.nombre || 'Usuario'}</span>
              <Badge variant={ticket.origen === 'colaborador' ? 'warning' : 'secondary'} className="text-[10px]">{ticket.origen === 'colaborador' ? 'Colaborador' : 'Interno'}</Badge>
              {ticket.autor?.empresa && <span className="text-xs text-slate-500">· {ticket.autor.empresa}</span>}
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Descripción</div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{ticket.descripcion}</p>
          </Card>

          {/* Info técnica */}
          {ticket.info_tecnica && (
            <Card className="p-0 border-slate-200 overflow-hidden">
              <button type="button" onClick={() => setTechAbierto((v) => !v)} className="w-full flex items-center justify-between px-5 py-3 text-sm text-slate-700 hover:bg-slate-50">
                <span className="font-medium">Información técnica capturada</span>
                {techAbierto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {techAbierto && (
                <dl className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 border-t border-slate-100 pt-3">
                  {Object.entries(ticket.info_tecnica).map(([k, v]) => (
                    <div key={k} className="text-[11px] min-w-0"><dt className="text-slate-400">{k}</dt><dd className="text-slate-700 font-mono break-all">{String(v)}</dd></div>
                  ))}
                </dl>
              )}
            </Card>
          )}

          {/* Adjuntos */}
          {adjuntos.length > 0 && (
            <Card className="p-6 border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Imágenes adjuntas ({adjuntos.length})</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {adjuntos.map((a) => <AdjuntoThumb key={a.id} adjunto={a} />)}
              </div>
            </Card>
          )}

          {/* Comentarios (2 pestañas) */}
          <Card className="p-6 border-slate-200">
            <div className="flex gap-1 border-b border-slate-200 mb-4">
              <button type="button" onClick={() => setTab('conv')} className={'px-4 py-2 text-sm -mb-px border-b-2 ' + (tab === 'conv' ? 'border-primary text-primary font-medium' : 'border-transparent text-slate-500 hover:text-slate-800')}>
                <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />Conversación
              </button>
              <button type="button" onClick={() => setTab('internas')} className={'px-4 py-2 text-sm -mb-px border-b-2 ' + (tab === 'internas' ? 'border-primary text-primary font-medium' : 'border-transparent text-slate-500 hover:text-slate-800')}>
                <Lock className="w-3.5 h-3.5 inline mr-1.5" />Notas internas
              </button>
            </div>

            {tab === 'conv' ? (
              <Comentarios
                items={ticket.comentarios_publicos} vacio="Sin mensajes con el usuario todavía."
                form={!terminal && puedeGestionar} valor={nuevoPub} setValor={setNuevoPub} guardando={guardando} enviando={accion === 'pub'}
                onEnviar={() => comentar(nuevoPub, false, setNuevoPub, 'pub')} botón="Enviar al usuario"
                hint="El usuario lo verá la próxima vez que abra el ticket (no se envía email)." terminal={terminal} />
            ) : (
              <Comentarios
                items={ticket.comentarios_internos} interna vacio="Sin notas internas."
                form={!terminal && puedeGestionar} valor={nuevaInt} setValor={setNuevaInt} guardando={guardando} enviando={accion === 'int'}
                onEnviar={() => comentar(nuevaInt, true, setNuevaInt, 'int')} botón="Añadir nota interna"
                hint="Solo visible para el equipo de soporte." terminal={terminal} />
            )}
          </Card>
        </div>

        {/* Columna lateral */}
        <div className="space-y-6">
          {puedeGestionar && (
            <Card className="p-5 border-slate-200">
              <div className="text-sm font-semibold text-slate-900 mb-4">Gestión del ticket</div>

              {/* Estado */}
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Estado</label>
              {terminal ? (
                <p className="text-xs text-slate-400 italic mb-4">Estado terminal — no admite cambios.</p>
              ) : (
                <>
                  <Select value={estadoSel} onChange={(e) => { setEstadoSel(e.target.value); setErrNota(null); }}>
                    <option value={ticket.estado}>{ESTADO_META[ticket.estado]?.label} (actual)</option>
                    {opcionesEstado.map((s) => <option key={s} value={s}>{ESTADO_META[s]?.label || s}</option>)}
                  </Select>
                  {estadoSel !== ticket.estado && (
                    <div className="mt-2 space-y-2">
                      <Textarea value={notaCambio} onChange={(e) => setNotaCambio(e.target.value)} rows={2} placeholder="Nota sobre el cambio (opcional)…" />
                      {errNota && <p className="text-xs text-red-600">{errNota}</p>}
                      {ESTADOS_EMAIL.includes(estadoSel) && (
                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                          <Mail className="w-3 h-3" />
                          Al guardar este cambio se notificará por email al usuario.
                        </p>
                      )}
                      <Button size="sm" onClick={guardarEstado} disabled={guardando}>
                        {accion === 'estado' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</> : 'Guardar cambio'}
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Asignado */}
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5 mt-5">
                Asignado a {accion === 'asignado' && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
              </label>
              <Select value={ticket.asignado?.id || ''} onChange={(e) => cambiarAsignado(e.target.value)} disabled={guardando}>
                <option value="">Sin asignar</option>
                {equipo.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </Select>

              {/* Prioridad (solo incidencia) */}
              {ticket.tipo === 'incidencia' && (
                <>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-1.5 mt-5">
                    Prioridad {accion === 'prioridad' && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
                  </label>
                  <Select value={ticket.prioridad || ''} onChange={(e) => cambiarPrioridad(e.target.value)} disabled={guardando}>
                    <option value="">Sin prioridad</option>
                    {PRIORIDADES.map((p) => <option key={p} value={p}>{PRIORIDAD_LABEL[p]}</option>)}
                  </Select>
                </>
              )}

              {/* Notas internas (legacy) */}
              <label className="block text-xs font-medium text-slate-700 mb-1.5 mt-5">Notas internas</label>
              <Textarea value={notasInput} onChange={(e) => setNotasInput(e.target.value)} rows={4} placeholder="Notas del equipo…" />
              <p className="text-[11px] text-slate-400 mt-1">No visibles para el usuario.</p>
              <Button size="sm" variant="outline" className="mt-2" onClick={guardarNotas} disabled={guardando}>
                {accion === 'notas' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</> : 'Guardar notas'}
              </Button>
            </Card>
          )}

          {/* Metadatos */}
          <Card className="p-5 border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Detalles</div>
            <ul className="space-y-2.5 text-sm">
              <Meta k="Número" v={<code className="font-mono text-xs">#{ticket.numero}</code>} />
              <Meta k="Tipo" v={ticket.tipo === 'incidencia' ? 'Incidencia' : 'Sugerencia'} />
              {ticket.categoria && <Meta k="Área" v={ticket.categoria.nombre} />}
              {ticket.modulo_intranet && <Meta k="Módulo" v={ticket.modulo_intranet} />}
              {ticket.tipo_dispositivo && <Meta k="Dispositivo" v={ticket.tipo_dispositivo} />}
              {ticket.tipo === 'incidencia' && <Meta k="Prioridad" v={ticket.prioridad ? PRIORIDAD_LABEL[ticket.prioridad] : '—'} />}
              <Meta k="Estado" v={<Badge variant={est.variant}>{est.label}</Badge>} />
              <Meta k="Asignado" v={ticket.asignado ? ticket.asignado.nombre : '—'} />
              <Meta k="Autor" v={ticket.autor?.nombre || '—'} />
              {/* 3.5.G — solo si lo abrió un técnico en nombre del autor (creado_por != null). */}
              {ticket.creado_por && (
                <Meta
                  k="Abierto por"
                  v={(
                    <span>
                      {ticket.creado_por_nombre || 'Técnico eliminado'}{' '}
                      <span className="text-slate-400">(en nombre de {ticket.autor?.nombre || 'Usuario eliminado'})</span>
                    </span>
                  )}
                />
              )}
              <Meta k="Creado" v={fecha(ticket.createdAt) || '—'} />
              <Meta k="Actualizado" v={fecha(ticket.updatedAt) || '—'} />
              {ticket.resuelto_at && <Meta k="Resuelto" v={fecha(ticket.resuelto_at)} />}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Comentarios({ items, interna, vacio, form, valor, setValor, onEnviar, boton, botón, hint, terminal, guardando, enviando }) {
  const label = boton || botón;
  return (
    <div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 italic">{vacio}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => (
            <li key={c.id} className={'rounded-lg p-3 ' + (interna ? 'bg-amber-50 border border-amber-100' : 'bg-slate-50')}>
              <div className="flex items-center gap-2 mb-1">
                {interna && <Lock className="w-3 h-3 text-amber-600" />}
                <span className="text-sm font-medium text-slate-900">{c.autor?.nombre || 'Usuario'}</span>
                <span className="text-[11px] text-slate-400">{hace(c.created_at)}</span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.contenido}</p>
            </li>
          ))}
        </ul>
      )}

      {terminal ? (
        <p className="mt-4 text-sm text-slate-400 italic">Ticket cerrado — no admite nuevos comentarios.</p>
      ) : form ? (
        <div className="mt-4">
          <Textarea value={valor} onChange={(e) => setValor(e.target.value)} rows={3} maxLength={5000} placeholder="Escribe un comentario…" />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-slate-400">{hint}</span>
            <Button size="sm" onClick={onEnviar} disabled={guardando || !valor.trim()}>
              {enviando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}{label}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdjuntoThumb({ adjunto }) {
  const src = useImagenBlob(`${process.env.REACT_APP_API_URL}${adjunto.download_url}`);
  return (
    <a href={src || undefined} target="_blank" rel="noopener noreferrer" title={adjunto.nombre_original}
      className="block aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-50">
      {src ? <img src={src} alt={adjunto.nombre_original} className="w-full h-full object-cover" />
        : <div className="w-full h-full grid place-items-center text-slate-300"><Loader2 className="w-5 h-5 animate-spin" /></div>}
    </a>
  );
}

function Meta({ k, v }) {
  return <li className="flex items-center justify-between gap-3"><span className="text-slate-500 shrink-0">{k}</span><span className="text-slate-900 text-right">{v}</span></li>;
}

// Skeleton del detalle: replica el grid real (principal 2/3 + lateral 1/3) para
// que no haya layout shift al llegar los datos. Genérico, no pixel-perfect.
function SkeletonDetalle() {
  return (
    <div className="px-6 py-6 min-h-full">
      <Skeleton className="h-4 w-44 mb-4" />
      <div className="flex items-start justify-between gap-3 mb-6">
        <div><Skeleton className="h-3 w-24 mb-2" /><Skeleton className="h-6 w-72" /></div>
        <div className="flex gap-2"><Skeleton className="h-5 w-20 rounded-full" /><Skeleton className="h-5 w-16 rounded-full" /></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Principal */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200">
            <div className="flex gap-2 mb-4"><Skeleton className="h-5 w-24 rounded-full" /><Skeleton className="h-4 w-32" /></div>
            <Skeleton className="h-3 w-28 mb-3" />
            <div className="space-y-2"><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-5/6" /><Skeleton className="h-3 w-2/3" /></div>
          </Card>
          <Card className="p-6 border-slate-200">
            <Skeleton className="h-3 w-40 mb-4" />
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="aspect-square rounded-md" />)}</div>
          </Card>
          <Card className="p-6 border-slate-200">
            <div className="flex gap-4 mb-4"><Skeleton className="h-4 w-28" /><Skeleton className="h-4 w-24" /></div>
            <div className="space-y-3">{[0, 1].map((i) => <div key={i} className="rounded-lg bg-slate-50 p-3"><Skeleton className="h-3 w-32 mb-2" /><Skeleton className="h-3 w-full" /></div>)}</div>
          </Card>
        </div>
        {/* Lateral */}
        <div className="space-y-6">
          <Card className="p-5 border-slate-200">
            <Skeleton className="h-4 w-32 mb-4" />
            {[0, 1, 2].map((i) => <div key={i} className="mb-4"><Skeleton className="h-3 w-20 mb-1.5" /><Skeleton className="h-9 w-full rounded-md" /></div>)}
            <Skeleton className="h-16 w-full rounded-md mb-2" /><Skeleton className="h-8 w-28 rounded-md" />
          </Card>
          <Card className="p-5 border-slate-200">
            <Skeleton className="h-3 w-24 mb-3" />
            <div className="space-y-2.5">{Array.from({ length: 7 }).map((_, i) => <div key={i} className="flex justify-between"><Skeleton className="h-3 w-16" /><Skeleton className="h-3 w-20" /></div>)}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
